import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  SlOption,
  SlSelect,
  SlButton,
  SlInput,
  SlIcon,
  SlDrawer,
} from "@shoelace-style/shoelace/dist/react";
import { debounce } from "../../utils/Debounce";
import API from "../../services/api";
import Paginator from "../../shared/Paginator";
import { Toast } from "../../shared/Toast";
import filterSvg from "../../assets/images/filter.svg";
import "./AlphaBrands.scss";

const LIMIT = 100;

function AlphaBrands() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState([]);
  const [loader, setLoader] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [showFilter, setShowFilter] = useState(false);

  // dropdown sources
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [subLoading, setSubLoading] = useState(false);
  const [salesOptions, setSalesOptions] = useState([]);

  // edit drawer
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({
    name: "",
    client_name: "",
    client_mobile: "",
    category: null,
    sub_category: null,
    sales: null,
  });
  const [saving, setSaving] = useState(false);

  // pending-approval drawer (record locked while a request is under review)
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingData, setPendingData] = useState(null);

  // mc_role === "2" is a checker-only b2b user; they approve via the
  // MakerChecker page and shouldn't see the edit action here.
  const canEdit = localStorage.getItem("mc_role") != 2;

  const onSearch = (e) => setSearch(e.target.value);

  const fetchList = async (page = 0) => {
    setLoader(true);
    try {
      const res = await API({
        url: "/alphabrands/getAlphaBrands",
        method: "POST",
        responseType: "json",
        data: { search, status, page, limit: LIMIT },
      });
      if (res.code === 200) {
        const data = res.data?.result?.data || [];
        const tc = res.data?.result?.totalCount || 0;
        setRows(data);
        setTotalCount(tc);
        setTotalPages(Math.ceil(tc / LIMIT));
      } else {
        setRows([]);
        setTotalCount(0);
        setTotalPages(0);
        Toast("error", res?.message);
      }
    } catch (err) {
      console.error(err);
    }
    setLoader(false);
  };

  const loadCategories = async () => {
    try {
      const res = await API({ url: "/birdview/newspapers_categories", method: "POST", responseType: "json" });
      if (res.code === 200) setCategories(res.data?.result || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadSales = async () => {
    try {
      const res = await API({ url: "/reports/allsalespersons", method: "POST", responseType: "json", data: {} });
      if (res.code === 200) setSalesOptions(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadSubCategories = async (category_id) => {
    if (category_id == null) {
      setSubCategories([]);
      return;
    }
    setSubLoading(true);
    try {
      const res = await API({
        url: "/birdview/alphasubcategory",
        method: "POST",
        responseType: "json",
        data: { category_id },
      });
      if (res.code === 200) setSubCategories(res.data || []);
    } catch (err) {
      console.error(err);
    }
    setSubLoading(false);
  };

  useEffect(() => {
    loadCategories();
    loadSales();
  }, []);

  useEffect(() => {
    setCurrentPage(0);
    fetchList(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status]);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
    fetchList(event.selected);
  };

  const openEdit = async (row) => {
    // Block editing if a maker-checker request is already pending for this row.
    try {
      const res = await API({
        url: "/alphabrands/editRequestPending",
        method: "POST",
        responseType: "json",
        data: { id: row.id },
      });
      if (res.code === 200 && res.data?.payload) {
        setPendingData(res.data);
        setPendingOpen(true);
        return;
      }
    } catch (err) {
      console.error(err);
    }
    setEditRow(row);
    setForm({
      name: row.name || "",
      client_name: row.client_name || "",
      client_mobile: row.client_mobile || "",
      category: row.category_id != null ? { value: row.category_id, label: row.category } : null,
      sub_category: row.sub_cat_id != null ? { value: row.sub_cat_id, label: row.sub_category } : null,
      sales: row.sales_person_id != null ? { value: row.sales_person_id, label: row.assigned_to } : null,
    });
    await loadSubCategories(row.category_id);
    setEditOpen(true);
  };

  const onCategoryChange = (selected) => {
    setForm((f) => ({ ...f, category: selected, sub_category: null }));
    loadSubCategories(selected?.value);
  };

  const submitEdit = async () => {
    if (!form.name || !form.name.trim()) {
      Toast("error", "Name is required");
      return;
    }
    if (!form.category) {
      Toast("error", "Category is required");
      return;
    }
    setSaving(true);
    try {
      const res = await API({
        url: "/alphabrands/editAlphaBrand",
        method: "POST",
        responseType: "json",
        data: {
          id: editRow.id,
          brand_id: editRow.brand_id,
          name: form.name.trim(),
          client_name: form.client_name?.trim() || null,
          client_mobile: form.client_mobile?.trim() || null,
          category_id: form.category?.value,
          category_name: form.category?.label,
          sub_cat_id: form.sub_category?.value ?? null,
          sub_category_name: form.sub_category?.label ?? null,
          sales_id: form.sales?.value ?? null,
          assigned_to: form.sales?.label ?? "Unassigned",
        },
      });
      if (res.code === 200) {
        Toast("success", res?.message);
        setEditOpen(false);
        setEditRow(null);
        fetchList(currentPage);
      } else {
        Toast("error", res?.message || res?.data?.message);
      }
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  // Portal react-select menus to <body> so they aren't clipped inside drawers.
  const rsStyles = {
    menu: (p) => ({ ...p, zIndex: 9999 }),
    menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  };
  const rsProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    styles: rsStyles,
  };

  const renderFilters = (inDrawer = false) => (
    <>
      <div className={inDrawer ? "w-full" : "min-w-[240px] flex-1"}>
        <label className="mb-1 block text-xs font-medium text-slate-500">Search</label>
        <SlInput
          className="w-full"
          size="medium"
          placeholder="Search name or mobile…"
          type="text"
          onSlInput={debounce(onSearch, 500)}
        >
          <SlIcon library="lucide" name="search" slot="prefix"></SlIcon>
        </SlInput>
      </div>
      <div className={inDrawer ? "w-full" : "w-44"}>
        <label className="mb-1 block text-xs font-medium text-slate-500">Status</label>
        <SlSelect
          className="w-full"
          placeholder="All"
          size="medium"
          value={status}
          onSlChange={(e) => setStatus(e.target.value)}
        >
          <SlOption value="all">All</SlOption>
          <SlOption value="active">Active</SlOption>
          <SlOption value="inactive">Inactive</SlOption>
        </SlSelect>
      </div>
    </>
  );

  return (
    <>
      <div className="alpha-brands-page flex h-[calc(100vh-72px)] flex-col">
        {/* ===== Header ===== */}
        <div className="flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h4 className="mb-0 text-2xl font-semibold text-slate-800">Alpha Brands</h4>
            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              {totalCount} Brands
            </span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={() => setShowFilter(true)}
              aria-label="Filters"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:border-sky-400 hover:text-sky-500 md:hidden"
            >
              <img src={filterSvg} alt="" className="h-4 w-4 opacity-70" />
            </button>
          </div>
        </div>

        {/* ===== Filters (desktop inline; mobile uses the drawer). mobHide
               instead of Tailwind `hidden` — a global `.hidden{display:none
               !important}` would beat `md:flex`. ===== */}
        <div className="mt-4 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-3 shrink-0 mobHide">
          {renderFilters(false)}
        </div>

        <p className="mt-4 mb-3 text-sm text-slate-500 shrink-0">
          Showing <span className="font-semibold text-slate-700">{rows?.length || 0}</span> of {totalCount} brands
        </p>

        {/* ===== Table ===== */}
        <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white">
          {loader ? (
            <div className="flex justify-center py-16">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : rows?.length ? (
            <table className="ab-table w-full text-sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Client Name</th>
                  <th>Mobile</th>
                  <th>Category</th>
                  <th>Sub Category</th>
                  {canEdit && <th className="text-right">Action</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="font-medium capitalize text-slate-800">{row.name || "—"}</span>
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            row.status === 1 ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {row.status === 1 ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </td>
                    <td className="text-slate-600 capitalize">{row.client_name || "—"}</td>
                    <td className="text-slate-600">{row.client_mobile || "—"}</td>
                    <td className="text-slate-600">{row.category || "—"}</td>
                    <td className="text-slate-600">{row.sub_category || "—"}</td>
                    {canEdit && (
                      <td className="text-right">
                        <sl-tooltip content="Edit">
                          <sl-icon-button library="lucide" name="edit" onClick={() => openEdit(row)}></sl-icon-button>
                        </sl-tooltip>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="py-16 text-center text-slate-400">No alpha brands found</div>
          )}
        </div>

        <div className="mt-3 flex shrink-0 justify-end">
          {totalPages > 1 && (
            <Paginator pageCount={totalPages} handlePageClick={handlePageClick} forcePage={currentPage} />
          )}
        </div>
      </div>

      {/* ===== Mobile filter drawer ===== */}
      {showFilter && (
        <SlDrawer
          open={showFilter}
          onSlRequestClose={() => setShowFilter(false)}
          placement="bottom"
          noHeader
          onSlAfterHide={() => setShowFilter(false)}
          style={{ "--size": "fit-content" }}
        >
          <div className="relative">
            <h3 className="mb-3 text-lg font-semibold text-slate-800">Filters</h3>
            <hr className="mb-4" />
            <div className="flex flex-col gap-4">{renderFilters(true)}</div>
            <SlIcon
              className="absolute -top-1 right-0 cursor-pointer text-2xl text-slate-400 hover:text-red-600"
              name="x-circle"
              onClick={() => setShowFilter(false)}
            ></SlIcon>
          </div>
        </SlDrawer>
      )}

      {/* ===== Edit drawer ===== */}
      {editOpen && (
        <SlDrawer
          label={`Edit ${editRow?.name || "Brand"}`}
          open={editOpen}
          onSlRequestClose={(e) => {
            if (e.detail.source === "overlay") e.preventDefault();
          }}
          onSlAfterHide={() => {
            setEditOpen(false);
            setEditRow(null);
          }}
          style={{ "--size": `${window.innerWidth <= 767 ? "100%" : "34vw"}` }}
        >
          <div className="ab-form flex flex-col gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Name</label>
              <input
                className="form-control"
                type="text"
                placeholder="Brand name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Client Name</label>
              <input
                className="form-control"
                type="text"
                placeholder="Client name"
                value={form.client_name}
                onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Mobile</label>
              <input
                className="form-control"
                type="text"
                placeholder="Mobile number"
                value={form.client_mobile}
                onChange={(e) => setForm((f) => ({ ...f, client_mobile: e.target.value }))}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Category</label>
              <Select
                placeholder="Select category"
                value={form.category}
                onChange={onCategoryChange}
                options={categories}
                {...rsProps}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Sub Category</label>
              <Select
                placeholder={subLoading ? "Loading…" : "Select sub category"}
                value={form.sub_category}
                onChange={(selected) => setForm((f) => ({ ...f, sub_category: selected }))}
                options={subCategories}
                isLoading={subLoading}
                isDisabled={!form.category}
                isClearable
                {...rsProps}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Assigned User</label>
              <Select
                placeholder="Select salesperson"
                value={form.sales}
                onChange={(selected) => setForm((f) => ({ ...f, sales: selected }))}
                options={salesOptions}
                isClearable
                {...rsProps}
              />
            </div>
            <div className="mt-2">
              <SlButton variant="primary" size="small" onClick={submitEdit} disabled={saving}>
                {saving && <span className="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>}
                {saving ? "Submitting…" : "Submit for Approval"}
              </SlButton>
            </div>
          </div>
        </SlDrawer>
      )}

      {/* ===== Pending-approval (locked) drawer ===== */}
      {pendingOpen && (
        <SlDrawer
          open={pendingOpen}
          label="Pending Approval Request"
          onSlRequestClose={() => setPendingOpen(false)}
          onSlAfterHide={() => setPendingOpen(false)}
          style={{ "--size": `${window.innerWidth <= 767 ? "100%" : "520px"}` }}
        >
          <div className="ab-pending flex flex-col gap-4">
            <div className="ab-pending-banner">
              <SlIcon library="lucide" name="alert-circle"></SlIcon>
              <div>
                <p className="ab-pending-title">
                  {pendingData?.user_name || "A user"}'s request is pending
                </p>
                <p className="ab-pending-sub">
                  This record is locked for editing until the pending request is reviewed.
                </p>
              </div>
            </div>
            <div>
              <h3 className="mb-2 text-base font-semibold text-slate-800">Requested Changes</h3>
              <div className="flex flex-col gap-2">
                {Object.entries(pendingData?.updated_payload || {}).map(([key, value]) => (
                  <div key={key} className="ab-pending-row">
                    <span className="ab-pending-key">{key}</span>
                    <span className="ab-pending-val">
                      {value === null || value === undefined || value === "" ? "—" : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-2 flex justify-end">
              <SlButton variant="default" size="small" onClick={() => setPendingOpen(false)}>
                Close
              </SlButton>
            </div>
          </div>
        </SlDrawer>
      )}
    </>
  );
}

export default AlphaBrands;
